import type { MaybeRefOrGetter } from 'vue';

import type { EntrySource, SelectedEntityInput } from '~/graphql/generated/graphql';

export interface SignupEntryContext {
  entrySource: EntrySource;
  selectedEntity?: SelectedEntityInput;
}

const SIGNUP_ENTRY_CONTEXT_STATE_KEY = 'signup-entry-context';

/**
 * Where the student currently is, in the terms signup cares about. Detail pages publish the
 * entity and source they already build their own CTAs from; surfaces that offer signup without
 * knowing the page — the global header — read it so their links carry the same entry lane.
 */
export const useSignupEntryContext = () => {
  const signupEntryContext = useState<SignupEntryContext | null>(SIGNUP_ENTRY_CONTEXT_STATE_KEY, () => null);

  const setSignupEntryContext = (context: SignupEntryContext) => {
    signupEntryContext.value = context;
  };

  // Identity-guarded: on a client-side navigation the incoming page's setup runs before the
  // outgoing page's scope is disposed, so an unconditional clear would wipe the new context.
  // Compared raw on both sides — shared state is deeply reactive, so what comes back out is a
  // proxy of what went in, and a caller reading from its own reactive data hands in a proxy too.
  const clearSignupEntryContext = (context?: SignupEntryContext) => {
    if (context && toRaw(signupEntryContext.value) !== toRaw(context)) {
      return;
    }
    signupEntryContext.value = null;
  };

  return { signupEntryContext, setSignupEntryContext, clearSignupEntryContext };
};

/**
 * Publishes a page's entry context for as long as that page is mounted. Pass a getter when the
 * context comes from a lazy fetch: on a client-side navigation the page's data lands after
 * setup, so a one-shot value would publish nothing until the next full load.
 */
export const useProvideSignupEntryContext = (context: MaybeRefOrGetter<SignupEntryContext | null | undefined>) => {
  const { setSignupEntryContext, clearSignupEntryContext } = useSignupEntryContext();
  let published: SignupEntryContext | undefined;

  watch(() => toValue(context), (next) => {
    if (published) {
      clearSignupEntryContext(published);
    }
    published = next ?? undefined;
    if (published) {
      setSignupEntryContext(published);
    }
  }, { immediate: true });

  onScopeDispose(() => {
    if (published) {
      clearSignupEntryContext(published);
    }
  });
};
